/**
 * Settings → Privacy: standing SDL consent + the revocation list (consent
 * surface spec D2.1 + D4). DARK: renders NOTHING unless the FE flag is on
 * (sdl-audience.js) — and the server independently answers uniform 404 while
 * its own flag is off.
 *
 * D4 discipline: revocation is one tap + one confirm, no friction copy, no
 * retention nag. Revoke = the server writes revokedAt (never a delete);
 * derived material is deleted via the cascade.
 *
 * Copy is the D6 PLACEHOLDER set — owner-ratified copy swaps in (new version
 * id) before any customer-visible flip.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@material-ui/core";
import { sdlConsentAvailable } from "./sdl-audience";
import { sdlConsentCopy } from "./sdl-consent-copy";
import {
  getStandingPreference,
  listOwnSessionConsents,
  revokeSessionConsent,
  setStandingPreference,
} from "./sdl-api";

const SCOPES = [
  { value: "TRANSCRIPT_ONLY", label: sdlConsentCopy.scopeTranscriptOnly },
  { value: "AUDIO_AND_TRANSCRIPT", label: sdlConsentCopy.scopeAudioAndTranscript },
];

export default function SdlStandingConsentSection() {
  const [preference, setPreference] = useState(null); // "OPTED_IN" | "DECLINED" | null
  const [scope, setScope] = useState("TRANSCRIPT_ONLY");
  const [consents, setConsents] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const enabled = sdlConsentAvailable();

  const refresh = useCallback(async () => {
    const [pref, list] = await Promise.all([
      getStandingPreference(),
      listOwnSessionConsents(),
    ]);
    if (pref.ok) {
      setPreference(pref.data?.preference ?? null);
      if (pref.data?.scope) {
        setScope(pref.data.scope);
      }
    }
    if (list.ok) {
      setConsents(list.data || []);
    }
    setError(pref.ok && list.ok ? null : "unavailable");
  }, []);

  useEffect(() => {
    if (enabled) {
      refresh();
    }
  }, [enabled, refresh]);

  if (!enabled) {
    return null; // dark posture: byte-identical absence (AC 2)
  }

  const writePreference = async (nextPreference, nextScope) => {
    setBusy(true);
    const res = await setStandingPreference(nextPreference, nextScope);
    if (res.ok) {
      setPreference(res.data?.preference ?? null);
      setError(null);
    } else {
      setError(res.sdlError);
    }
    setBusy(false);
  };

  const onToggleStanding = (event) => {
    if (event.target.checked) {
      writePreference("OPTED_IN", scope);
    } else {
      writePreference(null, null); // back to unset — the interstitial asks
    }
  };

  const onToggleDontAsk = (event) => {
    writePreference(event.target.checked ? "DECLINED" : null, null);
  };

  const onScopeChange = (event) => {
    setScope(event.target.value);
    if (preference === "OPTED_IN") {
      writePreference("OPTED_IN", event.target.value);
    }
  };

  const revokeOne = async (consentId) => {
    // D4: one tap + one confirm, nothing else.
    if (!window.confirm(sdlConsentCopy.revokeConfirm)) {
      return;
    }
    setBusy(true);
    const res = await revokeSessionConsent(consentId);
    if (!res.ok) {
      setError(res.sdlError);
    }
    await refresh();
    setBusy(false);
  };

  const revokeAll = async () => {
    if (!window.confirm(sdlConsentCopy.revokeAllConfirm)) {
      return;
    }
    setBusy(true);
    for (const row of consents.filter((c) => c.status === "GRANTED")) {
      const res = await revokeSessionConsent(row.consentId);
      if (!res.ok) {
        setError(res.sdlError);
        break;
      }
    }
    await refresh();
    setBusy(false);
  };

  const grantedRows = consents.filter((c) => c.status === "GRANTED");

  return (
    <Box data-testid="sdl-consent-section" mt={3}>
      <Divider />
      <Box mt={2}>
        <Typography variant="h6">{sdlConsentCopy.standingTitle}</Typography>
        <Typography variant="body2" color="textSecondary">
          {sdlConsentCopy.standingBody}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={preference === "OPTED_IN"}
              onChange={onToggleStanding}
              disabled={busy}
              inputProps={{ "data-testid": "sdl-standing-toggle" }}
            />
          }
          label={sdlConsentCopy.standingTitle}
        />
        {preference === "OPTED_IN" && (
          <Select
            value={scope}
            onChange={onScopeChange}
            disabled={busy}
            data-testid="sdl-scope-select"
          >
            {SCOPES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        )}
        {preference !== "OPTED_IN" && (
          <FormControlLabel
            control={
              <Checkbox
                checked={preference === "DECLINED"}
                onChange={onToggleDontAsk}
                disabled={busy}
                inputProps={{ "data-testid": "sdl-dont-ask-toggle" }}
              />
            }
            label="[PLACEHOLDER] Don't ask me before sessions"
          />
        )}
      </Box>

      <Box mt={2} data-testid="sdl-consent-list">
        <Typography variant="subtitle1">[PLACEHOLDER] Session consents</Typography>
        {consents.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            [PLACEHOLDER] No session consents yet.
          </Typography>
        )}
        {consents.map((row) => (
          <Box
            key={row.consentId}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            data-testid={`sdl-consent-row-${row.consentId}`}
          >
            <Typography variant="body2">
              {(row.grantedAt || "").slice(0, 10)} · {row.eventId} · {row.scope} ·{" "}
              {row.status}
            </Typography>
            {row.status === "GRANTED" && (
              <Button
                size="small"
                disabled={busy}
                onClick={() => revokeOne(row.consentId)}
                data-testid={`sdl-revoke-${row.consentId}`}
              >
                [PLACEHOLDER] Revoke
              </Button>
            )}
          </Box>
        ))}
        {grantedRows.length > 0 && (
          <Button size="small" disabled={busy} onClick={revokeAll} data-testid="sdl-revoke-all">
            [PLACEHOLDER] Revoke all
          </Button>
        )}
      </Box>

      {error && (
        <Typography variant="body2" color="error" data-testid="sdl-consent-error">
          [PLACEHOLDER] Something went wrong — please try again later.
        </Typography>
      )}
    </Box>
  );
}
